import BackspaceIcon from '@mui/icons-material/Backspace';
import EditIcon from '@mui/icons-material/Edit';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import PanToolIcon from '@mui/icons-material/PanTool';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-premium';
import { baseCell, booleanCell, dateCell } from 'components/cells';
import DataGridCell from 'components/cells/DataGridCell';
import { dateFilterOperators, filterOperators } from 'components/enhanced';
import { useTranslation } from 'react-i18next';
import { IsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import { IsTenantCoordinator } from 'shared/permissions/IsTenantCoordinator';
import { useIsTechnician } from 'shared/permissions/useIsTechnician';

export function PurchaseLinesColumns(
  reject,
  approve,
  preApprove,
  handleDelete,
  handleEdit,
  handleApproveAll,
  handleRejectedAll
): GridColDef[] {
  const { t } = useTranslation();

  const isTec = useIsTechnician();

  const renderPriceCell = (params) => {
    const rawValue = params.value;
    // Se è una stringa fatta solo di asterischi (es: "******", "**********")
    if (typeof rawValue === 'string' && /^\*+$/.test(rawValue.trim())) {
      return rawValue; // Già nascosto → lo lasciamo com'è
    }
    const numericValue = typeof rawValue === 'string' ? parseFloat(rawValue) : rawValue;
    const isValidNumber = typeof numericValue === 'number' && !isNaN(numericValue);

    if (isTec && isValidNumber) {
      return '**********';
    }

    return isValidNumber ? numericValue : rawValue;
  };

  const columns: GridColDef[] = [
    {
      field: 'index',
      headerName: t('global.count'),
      width: 50,
      filterable: false,
      sortable: false,
      editable: true
    },
    {
      field: 'is_periodic',
      headerName: t(`global.is_periodic`),
      width: 50,
      valueGetter: (params: GridRenderCellParams<any>) => (params.row.is_periodic ? 'SI' : 'NO'),
      renderCell: (params: GridRenderCellParams<any>) =>
        params.value === true && (
          <Tooltip title="is_periodic" placement="top">
            <EventRepeatIcon color="info" />
          </Tooltip>
        )
    },
    {
      field: 'status',
      headerName: t(`entities.purchase_request.status-field`),
      width: 100,
      filterable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <DataGridCell content={t(`entities.purchase_request.status.s-${params.value}`)} />
      )
    },
    {
      field: 'product',
      headerName: t('global.product'),
      width: 200,
      filterOperators,
      valueGetter: (params) => params.row.product?.code || '', // Usa solo `code`
      renderCell: (params: GridRenderCellParams<any>) => <DataGridCell content={params.value} />
    },
    {
      field: 'description',
      headerName: t(`entities.product.description`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'quantity',
      headerName: t(`entities.purchase_request.quantity`),
      width: 100,
      type: 'number',
      renderCell: baseCell
    },
    {
      field: 'static_unit_price',
      headerName: t(`entities.purchase_request.unit_price`),
      width: 100,
      type: 'number',
      renderCell: (params) => renderPriceCell(params)
    },
    {
      field: 'total_price',
      headerName: t(`entities.purchase_request.total_price`),
      width: 100,
      type: 'number',
      renderCell: (params) => renderPriceCell(params)
    },
    {
      field: 'contract',
      headerName: t(`global.contract`),
      width: 100,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'supplier',
      headerName: t(`global.supplier`),
      width: 100,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'discounted',
      headerName: t('entities.movement.discounted'),
      width: 100,
      type: 'boolean',
      renderCell: booleanCell(t)
    },
    {
      field: 'warehouse',
      headerName: t(`global.warehouse`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'requested_shipment_date',
      headerName: t(`entities.purchase_request.requested_shipment_date`),
      width: 200,
      filterOperators: dateFilterOperators,
      type: 'date',
      valueGetter: ({ value }) => (value ? new Date(value) : null),
      renderCell: dateCell
    },
    {
      field: 'note',
      headerName: t(`global.notes`),
      width: 200,
      filterOperators,
      valueGetter: (params) => params.value,
      renderCell: (params) => <Typography>{params.value}</Typography>
    },
    {
      field: 'actions',
      type: 'actions',
      width: 150,
      renderHeader: () => (
        <IsTenantAdministrator>
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <Tooltip title={t('actions.disapprove-all')}>
              <IconButton onClick={() => handleRejectedAll()} size="small">
                <PanToolIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('actions.approve-all')} placement="top">
              <IconButton onClick={() => handleApproveAll()} size="small">
                <VerifiedIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </IsTenantAdministrator>
      ),
      getActions: (params: GridRowParams) => [
        <IsTenantAdministrator key={1}>
          <Tooltip title={t('actions.disapprove')} placement="top">
            <IconButton onClick={() => reject(params.row)}>
              <PanToolIcon
                color={params.row.status === 4 ? 'warning' : 'disabled'}
                fontSize="small"
              />
            </IconButton>
          </Tooltip>
        </IsTenantAdministrator>,
        <IsTenantCoordinator key={2}>
          <IconButton onClick={() => preApprove(params.row)}>
            <Tooltip title={t('actions.pre-approve')} placement="top">
              <ThumbUpIcon fontSize="small" color={params.row.status === 2 ? 'info' : 'disabled'} />
            </Tooltip>
          </IconButton>
        </IsTenantCoordinator>,
        <IsTenantAdministrator key={3}>
          <IconButton onClick={() => approve(params.row)}>
            <Tooltip title={t('actions.approve')} placement="top">
              <VerifiedIcon
                fontSize="small"
                color={params.row.status === 3 ? 'success' : 'disabled'}
              />
            </Tooltip>
          </IconButton>
        </IsTenantAdministrator>,
        params.row.id && (
          <>
            <IconButton onClick={() => handleDelete(params.row)}>
              <Tooltip title={t('actions.delete')} placement="top" key={4}>
                <BackspaceIcon fontSize="small" />
              </Tooltip>
            </IconButton>

            <IconButton onClick={() => handleEdit(params.row)}>
              <Tooltip title={t('actions.edit')} key={4}>
                <EditIcon fontSize="small" />
              </Tooltip>
            </IconButton>
          </>
        )
      ]
    } as GridColDef
  ];
  return columns;
}
